import { useEffect, useState, useCallback } from 'react'
import { Car, Save, RotateCcw, Loader2, CheckCircle2, AlertTriangle, Power, Users, Terminal, MessageSquare, Bookmark, Trash2, RefreshCw, Eye, EyeOff, LogOut, FileText, Copy, ListOrdered, Plus, ArrowUp, ArrowDown, X } from 'lucide-react'
import { wreckfestAPI, pterodactylAPI } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import PageHeader from '@/components/PageHeader'
import {
  TRACKS, GAMEMODES, VEHICLE_DAMAGE, AI_DIFFICULTIES, CAR_CLASSES,
  GRID_ORDERS, SESSION_MODES, FREQUENCIES,
} from '@/lib/wreckfest-options'

/* Wreckfest Regie-page — bewerkt server_config.cfg via Pterodactyl-file-API.
   Wijzigingen worden pas actief na een restart (Wreckfest DS heeft geen
   hot-reload). "Opslaan + herstart" doet beide in één klik.

   Event-loop (playlist met meerdere tracks in rotatie) volgt in phase 2. */

const WRECKFEST_ID = 'a942aa0f'

/* Vervangt een leeg-string door de placeholder-tekst in select-labels. */
function labelOrDash(v) { return v === '' ? '(geen)' : v }

export default function WreckfestRegiePage({ embedded = false } = {}) {
  const [config, setConfig] = useState(null)
  const [draft, setDraft]   = useState({})
  const [status, setStatus] = useState(null)   // pterodactyl { state, mem_mb }
  const [live, setLive]     = useState(null)   // log-parsed { current_track, players, ... }
  const [presets, setPresets] = useState([])
  const [presetNaam, setPresetNaam] = useState('')
  const [zegBericht, setZegBericht] = useState('')
  const [logText, setLogText] = useState('')
  const [logAuto, setLogAuto] = useState(false)
  const [logMeta, setLogMeta] = useState(null)  // { bytes, lines }
  const [eventLoop, setEventLoop] = useState(null)   // Event[] uit backend
  const [elDraft, setElDraft] = useState(null)       // lokale werkkopij
  const [elBusy, setElBusy] = useState(false)
  const [busy, setBusy]     = useState(false)
  const [saveMsg, setSave]  = useState(null)
  const [err, setErr]       = useState(null)

  const laad = useCallback(async () => {
    setErr(null)
    try {
      const [cfgRes, srvRes] = await Promise.all([
        wreckfestAPI.getConfig(),
        pterodactylAPI.getServers(true).catch(() => ({ data: { servers: [] } })),
      ])
      setConfig(cfgRes.data.config)
      setDraft(cfgRes.data.config)
      const srv = (srvRes.data.servers || []).find(s => s.identifier === WRECKFEST_ID)
      if (srv) {
        const bytes = srv.resources?.memory_bytes ?? srv.memory_bytes ?? null
        setStatus({
          state: srv.status || srv.current_state || 'unknown',
          mem_mb: bytes != null ? Math.round(bytes / 1024 / 1024) : null,
        })
      }
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    }
  }, [])

  useEffect(() => { laad() }, [laad])

  // Live-monitoring + presets: apart, want polling / independent
  const laadLive = useCallback(async () => {
    try {
      const r = await wreckfestAPI.getStatus()
      setLive(r.data.status)
    } catch {}
  }, [])
  const laadPresets = useCallback(async () => {
    try {
      const r = await wreckfestAPI.listPresets()
      setPresets(r.data.presets || [])
    } catch {}
  }, [])
  const laadEventLoop = useCallback(async () => {
    try {
      const r = await wreckfestAPI.getEventLoop()
      setEventLoop(r.data.events || [])
      setElDraft(r.data.events || [])
    } catch {}
  }, [])

  useEffect(() => {
    laadLive(); laadPresets(); laadEventLoop()
    const iv = setInterval(laadLive, 5000)
    return () => clearInterval(iv)
  }, [laadLive, laadPresets, laadEventLoop])

  const laadLog = useCallback(async () => {
    try {
      const r = await wreckfestAPI.getLog(200)
      setLogText(r.data.log || '')
      setLogMeta({ bytes: r.data.bytes, lines: r.data.lines })
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    }
  }, [])
  useEffect(() => {
    if (!logAuto) return
    laadLog()
    const iv = setInterval(laadLog, 3000)
    return () => clearInterval(iv)
  }, [logAuto, laadLog])

  const setVeld = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  const veranderde = () => {
    if (!config) return {}
    const out = {}
    for (const [k, v] of Object.entries(draft)) {
      if (String(v ?? '') !== String(config[k] ?? '')) out[k] = v
    }
    return out
  }

  const opslaan = async (metHerstart) => {
    const changes = veranderde()
    if (Object.keys(changes).length === 0 && !metHerstart) {
      setSave('Geen wijzigingen om op te slaan.')
      return
    }
    setBusy(true); setSave(null); setErr(null)
    try {
      if (Object.keys(changes).length) {
        const r = await wreckfestAPI.updateConfig(changes)
        setConfig(r.data.config)
      }
      if (metHerstart) {
        await wreckfestAPI.restart()
        setSave('Opgeslagen en herstart getriggerd (server ~30s bezig).')
      } else {
        setSave(`Opgeslagen — ${Object.keys(changes).length} veld(en) gewijzigd. Herstart nodig om actief te worden.`)
      }
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  const power = async (signal) => {
    setBusy(true); setSave(null); setErr(null)
    try {
      await pterodactylAPI.sendPower(WRECKFEST_ID, signal)
      setSave(`Signal '${signal}' verstuurd.`)
      // korte pauze zodat state kan wisselen
      setTimeout(() => laad(), 2000)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  const cmd = async (command) => {
    setBusy(true); setSave(null); setErr(null)
    try {
      await wreckfestAPI.sendCommand(command)
      setSave(`Commando "${command}" verstuurd.`)
      setTimeout(laadLive, 1500)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const savePreset = async () => {
    if (!presetNaam.trim()) return
    setBusy(true); setSave(null); setErr(null)
    try {
      await wreckfestAPI.savePreset(presetNaam.trim())
      setSave(`Preset "${presetNaam}" opgeslagen.`)
      setPresetNaam('')
      laadPresets()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const loadPreset = async (slug) => {
    if (!window.confirm(`Preset "${slug}" laden overschrijft de huidige config en event loop. Doorgaan?`)) return
    setBusy(true); setSave(null); setErr(null)
    try {
      await wreckfestAPI.loadPreset(slug)
      // Een preset bevat de volledige server_config.cfg (incl. el_add-regels
      // voor de event loop) — we moeten dus BEIDE draft-stores verversen.
      await Promise.all([laad(), laadEventLoop()])
      setSave(`Preset "${slug}" geladen. Herstart nodig om actief te maken.`)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const delPreset = async (slug) => {
    if (!window.confirm(`Preset "${slug}" verwijderen?`)) return
    setBusy(true); setSave(null); setErr(null)
    try {
      await wreckfestAPI.deletePreset(slug)
      laadPresets()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const downloadPreset = async (slug) => {
    setBusy(true); setSave(null); setErr(null)
    try {
      const r = await wreckfestAPI.downloadPreset(slug)
      // Trigger download in browser
      const blob = new Blob([r.data], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `preset_${slug}.cfg`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  const uploadPreset = async (file) => {
    if (!file) return
    // Bestandsnaam als default; user mag overschrijven
    const defaultNaam = file.name.replace(/^preset_/, '').replace(/\.cfg$/i, '')
    const naam = window.prompt('Preset-naam:', defaultNaam)
    if (!naam || !naam.trim()) return
    setBusy(true); setSave(null); setErr(null)
    try {
      const content = await file.text()
      await wreckfestAPI.uploadPreset(naam.trim(), content)
      setSave(`Preset "${naam}" geïmporteerd.`)
      laadPresets()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  // Event-loop helpers
  const elAdd = () => setElDraft(list => [...(list || []), { track: TRACKS[0].value }])
  const elDel = (i) => setElDraft(list => list.filter((_, idx) => idx !== i))
  const elSetVeld = (i, k, v) => setElDraft(list => list.map((ev, idx) => idx === i ? { ...ev, [k]: v } : ev))
  const elVerplaats = (i, richting) => setElDraft(list => {
    const j = i + richting
    if (j < 0 || j >= list.length) return list
    const copy = [...list]
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
    return copy
  })
  const elGewijzigd = JSON.stringify(elDraft) !== JSON.stringify(eventLoop)
  const elSave = async () => {
    setElBusy(true); setSave(null); setErr(null)
    try {
      // Filter lege overrides (leeg string) — backend accepteert alleen niet-leeg
      const clean = (elDraft || []).map(ev => {
        const out = { track: ev.track }
        for (const k of ['gamemode','laps','bots','car_reset_disabled','wrong_way_limiter_disabled',
                          'car_class_restriction','car_restriction','weather','num_teams','time_limit']) {
          if (ev[k] != null && String(ev[k]).length) out[k] = ev[k]
        }
        return out
      })
      const r = await wreckfestAPI.updateEventLoop(clean)
      setEventLoop(r.data.events); setElDraft(r.data.events)
      setSave(`Event-loop opgeslagen (${r.data.events.length} events). Actief bij volgende race — schakel ook /eventloop aan indien nodig.`)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally { setElBusy(false) }
  }

  if (!config) {
    const inner = (
      <>
        {!embedded && <PageHeader icon={Car} title="Wreckfest-regie" />}
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
          {err ? <span className="text-signal">{err}</span> : 'Laden…'}
        </CardContent></Card>
      </>
    )
    return embedded ? inner : <div className="container mx-auto p-6">{inner}</div>
  }

  const changes = veranderde()
  const nChanges = Object.keys(changes).length

  const body = (
    <div className={embedded ? 'space-y-6' : 'container mx-auto p-6 space-y-6 max-w-4xl'}>
      {!embedded && (
        <div>
          <PageHeader icon={Car} title="Wreckfest-regie" />
          <p className="text-muted-foreground mt-2 text-sm">
            Bewerk server_config.cfg voor de Wreckfest-server. Wijzigingen worden pas
            actief na herstart.
          </p>
        </div>
      )}

      {err && (
        <div className="rounded border border-signal/40 bg-signal/10 p-3 text-sm text-signal flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {saveMsg && (
        <div className="rounded border border-live/40 bg-live/10 p-3 text-sm text-live flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> {saveMsg}
        </div>
      )}

      {/* Server-status */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 min-w-[15rem]">
            <div className={`h-2.5 w-2.5 rounded-full ${status?.state === 'running' ? 'bg-live' : status?.state === 'starting' ? 'bg-yellow-400 animate-pulse' : 'bg-muted-foreground/60'}`} />
            <div>
              <div className="font-semibold text-sm capitalize">{status?.state ?? 'onbekend'}</div>
              {status?.mem_mb != null && (
                <div className="text-[10px] text-muted-foreground">{status.mem_mb} MB · game 20716 / query 27017</div>
              )}
            </div>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => power('start')}>
              <Power className="h-3.5 w-3.5 mr-1" /> Start
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => power('restart')}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Herstart
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => power('stop')}>
              Stop
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live-monitoring (uit log.txt) */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">
              Live nu op de server
            </h3>
            <Button size="sm" variant="ghost" onClick={laadLive} title="Vernieuwen">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Track</div>
              <div className="font-mono text-sm truncate">{live?.current_track || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gamemode</div>
              <div className="font-mono text-sm truncate">{live?.current_gamemode || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Match</div>
              <div className="text-sm">
                <Badge variant={live?.match_state === 'racing' ? 'default' : 'outline'} className="uppercase">
                  {live?.match_state || 'onbekend'}
                </Badge>
                {live?.match_started_at && <span className="ml-2 font-mono text-xs text-muted-foreground">sinds {live.match_started_at}</span>}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-2">
              <Users className="h-3 w-3" /> Spelers ({live?.players?.length ?? 0})
            </div>
            {(live?.players?.length ?? 0) === 0 ? (
              <div className="text-sm text-muted-foreground">
                Geen spelers online. Klik <button onClick={() => cmd('/players')} className="underline">/players refresh</button> als de lijst leeg zou moeten zijn.
              </div>
            ) : (
              <ul className="space-y-1">
                {live.players.map(p => (
                  <li key={p.name} className="flex items-center gap-2 rounded border border-border/60 px-2 py-1">
                    <span className="flex-1 font-mono text-xs truncate">
                      {p.id != null && <span className="text-muted-foreground mr-1.5">#{p.id}</span>}
                      {p.name}
                    </span>
                    {p.id != null ? (
                      <>
                        <Button size="sm" variant="ghost" title="Spectator" disabled={busy}
                          onClick={() => cmd(`/spectateon ${p.id}`)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Terug in race" disabled={busy}
                          onClick={() => cmd(`/spectateoff ${p.id}`)}>
                          <EyeOff className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Kick" disabled={busy}
                          onClick={() => window.confirm(`Kick "${p.name}" (#${p.id})?`) && cmd(`/kick ${p.id}`)}>
                          <LogOut className="h-3.5 w-3.5 text-signal" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">geen ID — druk /players refresh</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {live?.last_event && (
            <div className="mt-3 text-[10px] text-muted-foreground font-mono truncate" title={live.last_event}>
              log-tail: {live.last_event}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Command-palette */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Terminal className="h-3.5 w-3.5" /> Server-commando's
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => cmd('/nextevent')}>Next event</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => cmd('/startvote')}>Track vote starten</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => cmd('/players')}>Player-list refreshen</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => cmd('/kickall')}>Alle spelers kicken</Button>
          </div>
          <div className="flex gap-2">
            <Input placeholder='Broadcast bericht naar spelers…' value={zegBericht}
              onChange={e => setZegBericht(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && zegBericht.trim()) { cmd(`/say ${zegBericht.trim()}`); setZegBericht('') } }} />
            <Button size="sm" disabled={busy || !zegBericht.trim()}
              onClick={() => { cmd(`/say ${zegBericht.trim()}`); setZegBericht('') }}>
              <MessageSquare className="h-3.5 w-3.5 mr-1" /> Zeg
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Commando's gaan naar Wreckfest-stdin. Als een commando niet werkt bestaat het niet
            in jouw Wreckfest-versie — verwijder gerust of stuur je eigen via /command.
          </p>
        </CardContent>
      </Card>

      {/* Ronde-instellingen */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">
            Ronde-instellingen
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Track">
              <SelectOrText value={draft.track} onChange={v => setVeld('track', v)}
                options={TRACKS.map(t => ({ value: t.value, label: t.label }))} />
            </Field>
            <Field label="Gamemode">
              <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                value={draft.gamemode || ''} onChange={e => setVeld('gamemode', e.target.value)}>
                {GAMEMODES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </Field>
            <Field label="Vehicle damage">
              <SimpleSelect value={draft.vehicle_damage} opts={VEHICLE_DAMAGE}
                onChange={v => setVeld('vehicle_damage', v)} />
            </Field>
            <Field label="Car class-restrictie">
              <SimpleSelect value={draft.car_class_restriction} opts={CAR_CLASSES}
                onChange={v => setVeld('car_class_restriction', v)}
                labelFor={labelOrDash} />
            </Field>
            <Field label="Enkel deze auto (technische naam)">
              <Input value={draft.car_restriction || ''} placeholder="(leeg = alle)"
                onChange={e => setVeld('car_restriction', e.target.value)} />
            </Field>
            <Field label="Laps">
              <Input type="number" min="1" max="60" value={draft.laps || ''}
                onChange={e => setVeld('laps', e.target.value)} />
            </Field>
            <Field label="Time limit (min)">
              <Input type="number" min="1" value={draft.time_limit || ''}
                onChange={e => setVeld('time_limit', e.target.value)} />
            </Field>
            <Field label="Bots">
              <Input type="number" min="0" max="24" value={draft.bots || ''}
                onChange={e => setVeld('bots', e.target.value)} />
            </Field>
            <Field label="AI difficulty">
              <SimpleSelect value={draft.ai_difficulty} opts={AI_DIFFICULTIES}
                onChange={v => setVeld('ai_difficulty', v)} />
            </Field>
            <Field label="Speciale voertuigen">
              <Toggle01 value={draft.special_vehicles_disabled} invert
                onChange={v => setVeld('special_vehicles_disabled', v)}
                aan="Toegestaan" uit="Verboden" />
            </Field>
            <Field label="Car reset">
              <Toggle01 value={draft.car_reset_disabled} invert
                onChange={v => setVeld('car_reset_disabled', v)}
                aan="Toegestaan" uit="Uitgeschakeld" />
            </Field>
            <Field label="Car-reset delay (s)">
              <Input type="number" min="0" max="20" value={draft.car_reset_delay || ''}
                onChange={e => setVeld('car_reset_delay', e.target.value)} />
            </Field>
            <Field label="Weer">
              <Input value={draft.weather || ''} placeholder="(leeg = willekeurig)"
                onChange={e => setVeld('weather', e.target.value)} />
            </Field>
            <Field label="Aantal teams">
              <Input type="number" min="2" max="4" value={draft.num_teams || ''}
                onChange={e => setVeld('num_teams', e.target.value)} />
            </Field>
            <Field label="Grid order">
              <SimpleSelect value={draft.grid_order} opts={GRID_ORDERS}
                onChange={v => setVeld('grid_order', v)} />
            </Field>
            <Field label="Wrong-way limiter">
              <Toggle01 value={draft.wrong_way_limiter_disabled} invert
                onChange={v => setVeld('wrong_way_limiter_disabled', v)}
                aan="Aan" uit="Uit" />
            </Field>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Deze instellingen worden gebruikt voor <em>single event</em>. Als de event loop
            aanstaat (zie hieronder) overschrijven de events in de playlist track/gamemode/laps/enz.
          </p>
        </CardContent>
      </Card>

      {/* Event loop — playlist met tracks in rotatie */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex-1 flex items-center gap-2">
              <ListOrdered className="h-3.5 w-3.5" /> Event loop (playlist)
              {elDraft && <Badge variant="outline" className="ml-2 normal-case tracking-normal text-[10px]">{elDraft.length} events</Badge>}
            </h3>
            <EventLoopStateBadge value={draft?.event_loop} />
          </div>
          {/* Event-loop config-toggle staat in de playlist-sectie zelf: dit
              is de enige plek waar ze inhoudelijk logisch is. De ronde-
              instellingen hierboven worden namelijk genegeerd als hij aan
              staat. Aan/uit + config-save + herstart zijn nodig. */}
          <div className="flex items-center gap-3 rounded border border-border/60 bg-card/50 p-2">
            <span className="text-xs font-semibold text-muted-foreground">Event loop actief bij server-start:</span>
            <Toggle01 value={draft?.event_loop}
              onChange={v => setVeld('event_loop', v)}
              aan="Aan" uit="Uit" />
            <span className="text-[10px] text-muted-foreground">
              (bewaar + herstart om te activeren)
            </span>
          </div>
          {elDraft == null ? (
            <div className="text-sm text-muted-foreground">Laden…</div>
          ) : elDraft.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              Nog geen playlist. Zonder events draait de server enkel de "single event"-instellingen hierboven.
            </div>
          ) : (
            <ol className="space-y-2">
              {elDraft.map((ev, i) => (
                <li key={i} className="rounded border border-border/60 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">#{i + 1}</Badge>
                    <div className="flex-1 min-w-0">
                      <SelectOrText value={ev.track}
                        onChange={v => elSetVeld(i, 'track', v)}
                        options={TRACKS.map(t => ({ value: t.value, label: t.label }))} />
                    </div>
                    <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => elVerplaats(i, -1)}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={i === elDraft.length - 1} onClick={() => elVerplaats(i, 1)}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => elDel(i)}>
                      <X className="h-3.5 w-3.5 text-signal" />
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-4 text-xs">
                    <label className="block">
                      <div className="text-[10px] text-muted-foreground mb-0.5">Gamemode</div>
                      <select className="w-full h-8 rounded border border-border bg-background px-2"
                        value={ev.gamemode ?? ''} onChange={e => elSetVeld(i, 'gamemode', e.target.value)}>
                        <option value="">(uit ronde-instellingen)</option>
                        {GAMEMODES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <div className="text-[10px] text-muted-foreground mb-0.5">Laps</div>
                      <Input type="number" min="1" max="60" className="h-8"
                        value={ev.laps ?? ''} placeholder="(default)"
                        onChange={e => elSetVeld(i, 'laps', e.target.value)} />
                    </label>
                    <label className="block">
                      <div className="text-[10px] text-muted-foreground mb-0.5">Bots</div>
                      <Input type="number" min="0" max="24" className="h-8"
                        value={ev.bots ?? ''} placeholder="(default)"
                        onChange={e => elSetVeld(i, 'bots', e.target.value)} />
                    </label>
                    <label className="block">
                      <div className="text-[10px] text-muted-foreground mb-0.5">Car class</div>
                      <select className="w-full h-8 rounded border border-border bg-background px-2"
                        value={ev.car_class_restriction ?? ''} onChange={e => elSetVeld(i, 'car_class_restriction', e.target.value)}>
                        <option value="">(default)</option>
                        <option value="a">A</option>
                        <option value="b">B</option>
                        <option value="c">C</option>
                      </select>
                    </label>
                    <label className="block sm:col-span-2">
                      <div className="text-[10px] text-muted-foreground mb-0.5">Enkel deze auto (car_restriction)</div>
                      <Input className="h-8" value={ev.car_restriction ?? ''} placeholder="(leeg = alle)"
                        onChange={e => elSetVeld(i, 'car_restriction', e.target.value)} />
                    </label>
                    <label className="block sm:col-span-2">
                      <div className="text-[10px] text-muted-foreground mb-0.5">Weer</div>
                      <Input className="h-8" value={ev.weather ?? ''} placeholder="(leeg = willekeurig)"
                        onChange={e => elSetVeld(i, 'weather', e.target.value)} />
                    </label>
                  </div>
                </li>
              ))}
            </ol>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={elAdd} disabled={elBusy}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Event toevoegen
            </Button>
            <div className="ml-auto text-xs">
              {elGewijzigd
                ? <span className="text-signal">Niet-opgeslagen wijzigingen</span>
                : <span className="text-muted-foreground">Geen wijzigingen</span>}
            </div>
            <Button size="sm" disabled={elBusy || !elGewijzigd} onClick={elSave}>
              {elBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Opslaan playlist
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Overrides zijn optioneel — leeg = valt terug op de "Ronde-instellingen" hierboven.
            Wreckfest rouleert automatisch door de lijst als <code>/eventloop</code> aan staat.
            Playlist-wijzigingen zijn actief vanaf de volgende event; geen server-herstart nodig.
          </p>
        </CardContent>
      </Card>

      {/* Lobby & moderatie */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">
            Lobby & moderatie
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Start-modus">
              <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                value={String(draft.admin_control ?? '0')}
                onChange={e => setVeld('admin_control', e.target.value)}>
                <option value="0">Automatisch — start bij ready-%</option>
                <option value="1">Handmatig — admin start countdown</option>
              </select>
            </Field>
            <Field label="Ready-% vereist (bij automatisch)">
              <Input type="number" min="0" max="100" value={draft.ready_players_required || ''}
                onChange={e => setVeld('ready_players_required', e.target.value)} />
            </Field>
            <Field label="Lobby countdown (s, 30-127)">
              <Input type="number" min="30" max="127" value={draft.lobby_countdown || ''}
                onChange={e => setVeld('lobby_countdown', e.target.value)} />
            </Field>
            <Field label="Track voting toestaan">
              <Toggle01 value={draft.enable_track_vote}
                onChange={v => setVeld('enable_track_vote', v)}
                aan="Aan" uit="Uit" />
            </Field>
            <Field label="Idle-players kicken">
              <Toggle01 value={draft.disable_idle_kick} invert
                onChange={v => setVeld('disable_idle_kick', v)}
                aan="Kicken" uit="Nooit kicken" />
            </Field>
            <Field label="Zichtbaar in Quickplay">
              <Toggle01 value={draft.exclude_from_quickplay} invert
                onChange={v => setVeld('exclude_from_quickplay', v)}
                aan="Zichtbaar" uit="Verborgen" />
            </Field>
            <Field label="Eerste speler = eigenaar">
              <Toggle01 value={draft.owner_disabled} invert
                onChange={v => setVeld('owner_disabled', v)}
                aan="Ja — geeft eigenaars-rechten" uit="Nee — geen eigenaar" />
            </Field>
            <Field label="Bij server-start user-rechten wissen">
              <Toggle01 value={draft.clear_users}
                onChange={v => setVeld('clear_users', v)}
                aan="Wissen" uit="Behouden" />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Admin Steam-IDs (steamID64, komma-gescheiden)">
              <Textarea rows={2} value={draft.admin_steam_ids || ''}
                placeholder="76561198…, 76561199…"
                onChange={e => setVeld('admin_steam_ids', e.target.value)} />
            </Field>
            <Field label="Moderator Steam-IDs (komma-gescheiden)">
              <Textarea rows={2} value={draft.op_steam_ids || ''}
                placeholder="76561198…, 76561199…"
                onChange={e => setVeld('op_steam_ids', e.target.value)} />
            </Field>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Vind je Steam-ID via <code>steamid.io</code> of in Wreckfest via <code>/players</code>.
            Admins kunnen commands uitvoeren; moderators kunnen kicken.
          </p>
        </CardContent>
      </Card>

      {/* Server-info (uitklap) */}
      <details className="rounded-lg border border-border">
        <summary className="cursor-pointer p-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          Server-info (zelden nodig)
        </summary>
        <div className="p-4 pt-0 grid gap-4 sm:grid-cols-2">
          <Field label="Servernaam">
            <Input value={draft.server_name || ''} maxLength={63}
              onChange={e => setVeld('server_name', e.target.value)} />
          </Field>
          <Field label="Welkomstbericht">
            <Input value={draft.welcome_message || ''} maxLength={254}
              onChange={e => setVeld('welcome_message', e.target.value)} />
          </Field>
          <Field label="Wachtwoord">
            <Input value={draft.password || ''} maxLength={31} placeholder="(geen)"
              onChange={e => setVeld('password', e.target.value)} />
          </Field>
          <Field label="Max spelers">
            <Input type="number" min="1" max="24" value={draft.max_players || ''}
              onChange={e => setVeld('max_players', e.target.value)} />
          </Field>
          <Field label="Session mode">
            <SimpleSelect value={draft.session_mode} opts={SESSION_MODES}
              onChange={v => setVeld('session_mode', v)} />
          </Field>
          <Field label="Update-frequency">
            <SimpleSelect value={draft.frequency} opts={FREQUENCIES}
              onChange={v => setVeld('frequency', v)} />
          </Field>
          <Field label="LAN-modus">
            <Toggle01 value={draft.lan}
              onChange={v => setVeld('lan', v)}
              aan="Aan" uit="Uit" />
          </Field>
        </div>
      </details>

      {/* Config-presets */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Bookmark className="h-3.5 w-3.5" /> Presets (config-snapshots)
            </h3>
            {presets.length > 0 && (
              /* Quick-select: 1 klik → preset actief maken. Sneller dan
                 helemaal naar de lijst scrollen. */
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Snel laden</span>
                <select
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  defaultValue=""
                  disabled={busy}
                  onChange={e => { if (e.target.value) { loadPreset(e.target.value); e.target.value = '' } }}>
                  <option value="">Kies preset…</option>
                  {presets.map(p => (
                    <option key={p.slug} value={p.slug}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Input placeholder="Naam (bv. 'Compo race', 'Vrije derby')"
              value={presetNaam} maxLength={40}
              onChange={e => setPresetNaam(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') savePreset() }}
              className="flex-1 min-w-[12rem]" />
            <Button size="sm" disabled={busy || !presetNaam.trim()} onClick={savePreset}>
              <Save className="h-3.5 w-3.5 mr-1" /> Bewaar huidige
            </Button>
            <label className="inline-flex items-center gap-1 h-9 px-3 text-sm rounded-md border border-input bg-background cursor-pointer hover:border-acid/50 transition-colors">
              <Plus className="h-3.5 w-3.5" /> Importeer .cfg
              <input type="file" accept=".cfg,.txt,text/plain" className="hidden"
                     onChange={e => { uploadPreset(e.target.files?.[0]); e.target.value = '' }}
                     disabled={busy} />
            </label>
          </div>
          {presets.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nog geen presets opgeslagen.</p>
          ) : (
            <ul className="space-y-1">
              {presets.map(p => (
                <li key={p.slug} className="flex items-center gap-2 rounded border border-border/60 p-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate capitalize">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {p.slug} · {new Date(p.modified_at).toLocaleString('nl-BE', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => loadPreset(p.slug)}>
                    Laden
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => downloadPreset(p.slug)} title="Download als .cfg">
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => delPreset(p.slug)}>
                    <Trash2 className="h-3.5 w-3.5 text-signal" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-muted-foreground">
            Een preset bewaart de huidige <code>server_config.cfg</code> als apart bestand. "Laden"
            zet die terug als actieve config — herstart nodig om actief te worden. Import verwacht
            een raw <code>.cfg</code>-tekstbestand (zoals de download).
          </p>
        </CardContent>
      </Card>

      {/* Log-tail viewer — voor onderzoek naar wat Wreckfest écht logt */}
      <details className="rounded-lg border border-border" onToggle={(e) => {
        if (e.currentTarget.open && !logText) laadLog()
      }}>
        <summary className="cursor-pointer p-4 text-sm font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <FileText className="h-3.5 w-3.5" />
          Log-tail (raw log.txt)
          {logMeta && <span className="ml-auto text-[10px] normal-case tracking-normal font-normal text-muted-foreground">{logMeta.lines} regels · {logMeta.bytes} B</span>}
        </summary>
        <div className="p-4 pt-0 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Button size="sm" variant="outline" onClick={laadLog} disabled={busy}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Vernieuwen
            </Button>
            <label className="text-xs flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={logAuto} onChange={e => setLogAuto(e.target.checked)} />
              Auto-refresh (3s)
            </label>
            <Button size="sm" variant="ghost" disabled={!logText}
              onClick={() => { navigator.clipboard.writeText(logText); setSave('Log gekopieerd naar klembord.') }}>
              <Copy className="h-3.5 w-3.5 mr-1" /> Kopieer
            </Button>
          </div>
          <pre className="rounded bg-black/60 p-3 text-[11px] font-mono text-green-300 overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre">
            {logText || '(log leeg — druk Vernieuwen)'}
          </pre>
          <p className="text-[10px] text-muted-foreground">
            Nuttig om te ontdekken wat Wreckfest écht schrijft tijdens een race (finish-positions,
            lap-times, chat, join/leave). Kopieer een stukje race-log en stuur het door — dan wordt
            de parser uitgebreid.
          </p>
        </div>
      </details>

      {/* Save-bar */}
      <div className="sticky bottom-4 flex items-center gap-3 rounded-lg border border-border bg-background/95 backdrop-blur p-3 shadow-lg">
        <div className="text-sm">
          {nChanges === 0
            ? <span className="text-muted-foreground">Geen wijzigingen</span>
            : <span><Badge variant="outline">{nChanges}</Badge> veld(en) gewijzigd</span>}
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" disabled={busy || nChanges === 0} onClick={() => opslaan(false)}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Opslaan
          </Button>
          <Button size="sm" disabled={busy} onClick={() => opslaan(true)}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Opslaan + Herstart
          </Button>
        </div>
      </div>
    </div>
  )
  return body
}

/* Kleine badge die de huidige event_loop-config-waarde toont. Leest uit
   de server_config.cfg (via draft), niet uit runtime (Wreckfest geeft
   die state niet expliciet in de log — /eventloop-command toggelt runtime,
   maar bij herstart valt de server terug op deze config-waarde). */
function EventLoopStateBadge({ value, className = '' }) {
  const raw = String(value ?? '').trim().toLowerCase()
  const aan = raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on'
  const kleur = raw === '' ? 'bg-muted text-muted-foreground'
              : aan ? 'bg-live/20 text-live border border-live/40'
                    : 'bg-signal/10 text-signal border border-signal/30'
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono uppercase ${kleur} ${className}`}
          title={raw === '' ? 'event_loop-key niet in config' : `event_loop=${raw}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${aan ? 'bg-live' : raw === '' ? 'bg-muted-foreground' : 'bg-signal'}`} />
      {raw === '' ? '?' : aan ? 'aan' : 'uit'}
    </span>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  )
}

function SimpleSelect({ value, opts, onChange, labelFor = (v) => v }) {
  return (
    <select className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
      value={value ?? ''} onChange={e => onChange(e.target.value)}>
      {opts.map(v => <option key={v} value={v}>{labelFor(v)}</option>)}
    </select>
  )
}

/* Wreckfest gebruikt 0=aan, 1=disabled voor sommige velden. Met `invert`
   flippen we de UI-labels zodat "Aan" 0 opslaat en "Uit" 1. */
function Toggle01({ value, onChange, aan, uit, invert = false }) {
  const isAan = invert ? String(value) === '0' : String(value) === '1'
  const wissel = () => {
    const nieuwe = isAan ? (invert ? '1' : '0') : (invert ? '0' : '1')
    onChange(nieuwe)
  }
  return (
    <button type="button" onClick={wissel}
      className={`w-full h-9 rounded-md border px-3 text-sm text-left transition-colors ${isAan
        ? 'border-live/40 bg-live/10 text-live'
        : 'border-border bg-background text-muted-foreground'
      }`}>
      {isAan ? aan : uit}
    </button>
  )
}

/* Voor track: select + optie om een vrije naam in te tikken (want er zijn
   30+ tracks en niet alle in de shortlist). */
function SelectOrText({ value, onChange, options }) {
  const bekend = options.some(o => o.value === value)
  const [modus, setModus] = useState(bekend || !value ? 'select' : 'text')
  return (
    <div className="flex gap-2">
      {modus === 'select' ? (
        <select className="flex-1 h-9 rounded-md border border-border bg-background px-2 text-sm"
          value={value ?? ''} onChange={e => onChange(e.target.value)}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input className="flex-1" value={value ?? ''} onChange={e => onChange(e.target.value)}
          placeholder="track technische naam" />
      )}
      <Button type="button" size="sm" variant="outline"
        onClick={() => setModus(m => m === 'select' ? 'text' : 'select')}>
        {modus === 'select' ? 'Vrij' : 'Lijst'}
      </Button>
    </div>
  )
}
